import * as React from 'react';
import classnames from 'classnames';
import { DateTime } from 'luxon';
import dataAtIds from '../dataAtIds.json';
import Flex, { Props as FlexProps } from '../Flex/Flex';

interface Props extends FlexProps {
  dateSelectableFrom?: DateTime;
  dateSelectableTo?: DateTime;
  dateSelectedFrom?: DateTime;
  dateSelectedTo?: DateTime;
  n: number;
  onSelect: (date: DateTime) => void;
  viewDate: DateTime;
}

const DatePickerDay: React.FunctionComponent<Props> = (props: Props) => {
  const {
    dateSelectableFrom,
    dateSelectableTo,
    dateSelectedFrom,
    dateSelectedTo,
    n,
    onSelect,
    viewDate,
    ...rest
  } = props;

  const date = viewDate.plus({ days: n - viewDate.weekday });

  const dayOfWeek = viewDate.weekday;
  const daysInMonth = viewDate.daysInMonth;
  const daysInPreviousMonth = viewDate.minus({ month: 1 }).daysInMonth;

  const isPreviousMonth = n < dayOfWeek;
  const isNextMonth = n - dayOfWeek >= daysInMonth;
  const isCurrentMonth = !isPreviousMonth && !isNextMonth;
  const isAfterSelectableFromDate = !dateSelectableFrom || date.equals(dateSelectableFrom) || date > dateSelectableFrom;
  const isBeforeSelectableToDate = !dateSelectableTo || date.equals(dateSelectableTo) || date < dateSelectableTo;
  const isSelectable = isCurrentMonth && isAfterSelectableFromDate && isBeforeSelectableToDate;

  const hasSelectionRange = dateSelectedFrom && dateSelectedTo && !dateSelectedFrom.equals(dateSelectedTo);

  const isSelectedFrom = isCurrentMonth && (dateSelectedFrom && date.equals(dateSelectedFrom));
  const isSelectedTo = isCurrentMonth && (dateSelectedTo && date.equals(dateSelectedTo));
  const isSelectedBetween = isCurrentMonth && dateSelectedFrom && dateSelectedTo && hasSelectionRange && (+date > +dateSelectedFrom && +date < +dateSelectedTo);

  const classes = classnames('DatePicker__day', {
    'DatePicker__day--selected': isSelectedFrom || isSelectedTo,
    'DatePicker__day--selected-between': isSelectedBetween,
    'DatePicker__day--selected-from': hasSelectionRange && isSelectedFrom,
    'DatePicker__day--selected-to': hasSelectionRange && isSelectedTo,
    'DatePicker__day--selectable': isSelectable,
    'DatePicker__day--unselected': !isSelectedFrom && !isSelectedTo && !isSelectedBetween,
    'DatePicker__day--unselectable': !isSelectable,
  });

  return (
    <Flex { ...rest }
        alignChildren="middle"
        className={ classes }
        data-bitkit-at-id={ isSelectable ? dataAtIds.DatePickerDay : undefined }
        direction="horizontal"
        onClick={ isSelectable ? () => onSelect(date) : undefined }>
      <Flex
          alignChildren="middle"
          className="DatePicker__day-inner"
          direction="horizontal">
        { isPreviousMonth && daysInPreviousMonth - (dayOfWeek - n) }
        { isCurrentMonth && n - dayOfWeek + 1 }
        { isNextMonth && n - dayOfWeek - daysInMonth + 1 }
      </Flex>
    </Flex>
  );
};

export default DatePickerDay;
